/**
 * Një proces onboarding-u, i hapur.
 *
 * Pyetja që i përgjigjet kjo faqe nuk është «sa përqind është bërë», por «kush
 * e ka secilën pjesë dhe çfarë e ndal». Prandaj detyrat janë të grupuara sipas
 * përgjegjësisë — HR, IT, menaxheri, trajnimi, vetë punonjësi — dhe çdo grup e
 * mban numrin e vet. Pa atë ndarje, një listë me njëzet e dy rreshta i përket
 * të gjithëve dhe askujt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, LinearProgress, MenuItem, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link, useHistory, useParams } from 'react-router-dom';
import usePermissions from '../hooks/usePermissions';
import onboardingService from '../services/onboardingService';
import myWorkService, { documentTypeLabel, documentStatusMeta } from '../services/myWorkService';
import WorkerPicker from '../components/common/WorkerPicker';
import apiService from '../services/apiServices';
import {
  CATEGORIES, categoryLabel, roleLabel, instanceMeta, taskMeta, TASK_CLOSED, dayOneText,
} from '../config/onboarding';

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

const ACTIVITY_TEXT = {
  created: (e) => `Procesi u krijua me ${e.taskCount} detyra`,
  task_status: (e) => `«${e.title}» → ${taskMeta(e.to).label}${e.reason ? ` · ${e.reason}` : ''}`,
  task_reassigned: (e) => `«${e.title}» iu kalua ${roleLabel(e.toRole)}`,
  task_due_changed: (e) => `Afati i «${e.title}»: ${e.from || '—'} → ${e.to}`,
  start_date_changed: (e) => `Dita e parë: ${e.from} → ${e.to} (${e.tasksShifted} afate u zhvendosën)`,
  worker_linked: () => 'U lidh me punonjësin te Stafi',
  completed: () => 'Procesi u mbyll',
  cancelled: (e) => `Procesi u anulua — ${e.reason}`,
  reopened: (e) => `Procesi u rihap — ${e.reason}`,
};

export default function OnboardingDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const { can } = usePermissions();
  const canEdit = can('onboarding.edit') || can('onboarding.manage');
  const canComplete = can('onboarding.complete') || can('onboarding.manage');
  const canManage = can('onboarding.manage');

  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [blocker, setBlocker] = useState(null);   // çfarë e ndalon mbylljen
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);     // { kind, taskId, title }
  const [reason, setReason] = useState('');
  const [linkWorker, setLinkWorker] = useState('');
  // Lista e punonjësve ngarkohet vetëm kur procesi ende s'është i lidhur —
  // përndryshe do të ishte një kërkesë e kotë për çdo hapje.
  const [workers, setWorkers] = useState([]);
  // Dokumentet e ngarkuara nga punonjësi — HR-ja i shqyrton këtu, në të njëjtin
  // vend ku sheh detyrën që i kërkoi.
  const [docs, setDocs] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const r = await onboardingService().detail(id);
    if (r?.error) setError(r.error); else setD(r);
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.id) myWorkService().documentsOf(d.id).then((r) => setDocs(Array.isArray(r) ? r : []));
  }, [d?.id]);
  useEffect(() => {
    if (d && !d.workerId && workers.length === 0) {
      apiService().get('workers').then((r) => setWorkers(r?.data || [])).catch(() => {});
    }
  }, [d, workers.length]);

  const run = async (fn) => {
    setBusy(true); setError(''); setBlocker(null);
    const r = await fn();
    setBusy(false);
    if (r?.error) {
      setError(r.error);
      if (r.details) setBlocker(r.details);
      return null;
    }
    await load();
    return r;
  };

  if (loading) return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
  if (!d) return <Alert severity="error" sx={{ m: 2 }}>{error || 'Procesi nuk u gjet.'}</Alert>;

  const st = instanceMeta(d.status);
  const closed = ['completed', 'cancelled'].includes(d.status);
  const byCategory = CATEGORIES
    .map((c) => ({ ...c, tasks: d.tasks.filter((t) => t.category === c.code) }))
    .filter((c) => c.tasks.length);

  const submitDialog = async () => {
    const k = dialog.kind;
    const r = await run(() => {
      if (k === 'cancel') return onboardingService().cancel(d.id, reason);
      if (k === 'reopen') return onboardingService().reopen(d.id, reason);
      if (k === 'block') return onboardingService().setTaskStatus(dialog.taskId, 'blocked', { reason });
      if (k === 'skip') return onboardingService().setTaskStatus(dialog.taskId, 'skipped', { reason });
      return Promise.resolve({ error: 'Veprim i panjohur' });
    });
    if (r) { setDialog(null); setReason(''); }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push('/onboarding')}
          sx={{ textTransform: 'none' }}>Onboarding</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {/* Mbyllja e refuzuar nuk mbetet si «nuk u krye»: thotë saktësisht çfarë mungon. */}
      {blocker && (blocker.pending || blocker.blocked) && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setBlocker(null)}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Mbetën pa u bërë:</Typography>
          {(blocker.pending || blocker.blocked || []).map((t, i) => (
            <Typography key={i} variant="caption" display="block">• {t}</Typography>
          ))}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{d.employee}</Typography>
                <Chip size="small" color={st.color} label={st.label} sx={{ height: 21, fontWeight: 700 }} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {[d.position, d.departmentName, `fillimi ${fmtDate(d.startDate)}`].filter(Boolean).join(' · ')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pronari: {d.ownerName || '—'}{d.templateName ? ` · shablloni: ${d.templateName}` : ''}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 190 }}>
              <Typography variant="caption" color="text.secondary">
                Përparimi {d.progress.completed}/{d.progress.total} ({d.progress.percent}%)
              </Typography>
              <LinearProgress variant="determinate" value={d.progress.percent} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Të detyrueshme: {d.progress.requiredCompleted}/{d.progress.requiredTotal}
                {d.progress.overdue ? ` · ${d.progress.overdue} me vonesë` : ''}
              </Typography>
            </Box>

            <Stack spacing={0.5}>
              {!closed && canComplete && (
                <Tooltip title={d.completion.ok ? 'Të gjitha detyrat e detyrueshme janë kryer' : d.completion.error}>
                  <span>
                    <Button size="small" variant="contained" disabled={busy || !d.completion.ok}
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => run(() => onboardingService().complete(d.id))}
                      sx={{ textTransform: 'none' }}>Mbyll procesin</Button>
                  </span>
                </Tooltip>
              )}
              {!closed && canEdit && (
                <Button size="small" color="inherit" disabled={busy}
                  onClick={() => { setDialog({ kind: 'cancel' }); setReason(''); }}
                  sx={{ textTransform: 'none' }}>Anulo</Button>
              )}
              {closed && canManage && (
                <Button size="small" variant="outlined" disabled={busy}
                  onClick={() => { setDialog({ kind: 'reopen' }); setReason(''); }}
                  sx={{ textTransform: 'none' }}>Rihap</Button>
              )}
            </Stack>
          </Stack>

          {/* Gatishmëria për ditën e parë — listë, jo pikë. */}
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Gatishmëria për ditën e parë:</Typography>
              <Chip size="small" color={!d.dayOne.defined ? 'default' : d.dayOne.ready ? 'success' : 'warning'}
                label={dayOneText(d.dayOne)} sx={{ height: 20, fontSize: '.68rem' }} />
            </Stack>
            {d.dayOne.missing.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Mungojnë: {d.dayOne.missing.map((m) => m.title).join(' · ')}
              </Typography>
            )}
          </Box>

          {/* Stafi mbetet burimi i së vërtetës; asnjë punonjës nuk krijohet këtu. */}
          {!d.workerId && (
            <Alert severity="warning" sx={{ mt: 2 }}
              action={canEdit ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 260 }}>
                  <Box sx={{ flex: 1 }}>
                    <WorkerPicker size="small" label="Punonjësi" workers={workers} value={linkWorker} onChange={setLinkWorker} />
                  </Box>
                  <Button size="small" disabled={!linkWorker || busy}
                    onClick={() => run(() => onboardingService().linkWorker(d.id, linkWorker))}>Lidh</Button>
                </Stack>
              ) : null}>
              Punonjësi ende nuk është lidhur me Stafin. Paga dhe kontrata varen nga ajo lidhje.
            </Alert>
          )}
          {d.workerId && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              <Link to={`/workerlist/editworker/${d.workerId}`}>Hap dosjen e punonjësit</Link>
              {d.applicationId ? <> · <Link to={`/applications/edit/${d.applicationId}`}>aplikimi i rekrutimit</Link></> : null}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ── Detyrat sipas përgjegjësisë ──────────────────────────────────── */}
      {byCategory.map((c) => {
        const done = c.tasks.filter((t) => ['completed', 'skipped'].includes(t.status)).length;
        return (
          <Card key={c.code} sx={{ mb: 1.5 }}>
            <CardContent>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '.92rem' }}>{c.label}</Typography>
                <Typography variant="caption" color="text.secondary">{done}/{c.tasks.length}</Typography>
              </Stack>
              <Stack spacing={0.75}>
                {c.tasks.map((t) => {
                  const m = taskMeta(t.status);
                  const isClosed = TASK_CLOSED.includes(t.status);
                  return (
                    <Stack key={t.id} direction={{ xs: 'column', sm: 'row' }} spacing={1}
                      alignItems={{ sm: 'center' }}
                      sx={{ p: 1, border: '1px solid', borderColor: t.overdue ? '#F59E0B' : 'divider', borderRadius: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t.title}</Typography>
                          {t.required
                            ? <Chip size="small" label="e detyrueshme" sx={{ height: 17, fontSize: '.6rem' }} />
                            : <Chip size="small" variant="outlined" label="opsionale" sx={{ height: 17, fontSize: '.6rem' }} />}
                          {t.critical && <Chip size="small" color="error" variant="outlined" label="dita 1" sx={{ height: 17, fontSize: '.6rem' }} />}
                          {t.documentRequired && <Chip size="small" variant="outlined" label="dokument" sx={{ height: 17, fontSize: '.6rem' }} />}
                          {t.assetRequired && <Chip size="small" variant="outlined" label={t.assetRequired} sx={{ height: 17, fontSize: '.6rem' }} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {roleLabel(t.assignedRole)}{t.assigneeName ? ` · ${t.assigneeName}` : ' · pa përgjegjës'}
                          {t.dueDate ? ` · afati ${fmtDate(t.dueDate)}` : ''}
                          {t.overdue ? ' · me vonesë' : ''}
                        </Typography>
                        {t.blockedReason && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#B45309' }}>Bllokuar: {t.blockedReason}</Typography>
                        )}
                        {t.skipReason && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Kapërcyer: {t.skipReason}</Typography>
                        )}
                      </Box>
                      <Chip size="small" color={m.color} variant="outlined" label={m.label} sx={{ height: 20, fontSize: '.66rem' }} />
                      {!closed && (
                        <Stack direction="row" spacing={0.5}>
                          {!isClosed && (
                            <Button size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                              onClick={() => run(() => onboardingService().setTaskStatus(t.id, 'completed'))}>Kryer</Button>
                          )}
                          {!isClosed && t.status !== 'blocked' && (
                            <Button size="small" color="warning" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                              onClick={() => { setDialog({ kind: 'block', taskId: t.id, title: t.title }); setReason(''); }}>Blloko</Button>
                          )}
                          {!isClosed && (
                            <Button size="small" color="inherit" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                              onClick={() => { setDialog({ kind: 'skip', taskId: t.id, title: t.title }); setReason(''); }}>Kapërce</Button>
                          )}
                          {isClosed && t.status !== 'cancelled' && canEdit && (
                            <Button size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                              onClick={() => run(() => onboardingService().setTaskStatus(t.id, 'pending'))}>Rihap</Button>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {/* ── Dokumentet ───────────────────────────────────────────────────── */}
      {docs.length > 0 && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 800, fontSize: '.92rem', mb: 1 }}>Dokumentet</Typography>
            <Stack spacing={0.75}>
              {docs.map((doc) => {
                const sm = documentStatusMeta(doc.status);
                return (
                  <Stack key={doc.id} direction={{ xs: 'column', sm: 'row' }} spacing={1}
                    alignItems={{ sm: 'center' }}
                    sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{doc.originalName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {documentTypeLabel(doc.documentType)}
                        {doc.taskTitle ? ` · ${doc.taskTitle}` : ''} · {fmtDate(doc.uploadedAt)}
                        {doc.uploadedBy ? ` · ${doc.uploadedBy}` : ''}
                        {doc.rejectReason ? ` · ${doc.rejectReason}` : ''}
                      </Typography>
                    </Box>
                    <Chip size="small" color={sm.color} variant="outlined" label={sm.label} sx={{ height: 20, fontSize: '.66rem' }} />
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" sx={{ textTransform: 'none', fontSize: '.72rem' }}
                        onClick={() => myWorkService().downloadDocument(doc.id, doc.originalName)}>Shkarko</Button>
                      {canEdit && doc.status === 'pending' && (
                        <>
                          <Button size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                            onClick={() => run(async () => {
                              const r = await myWorkService().reviewDocument(doc.id, 'accepted');
                              if (!r?.error) setDocs(await myWorkService().documentsOf(d.id));
                              return r;
                            })}>Prano</Button>
                          <Button size="small" color="error" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                            onClick={() => { setReviewFor(doc); setReason(''); }}>Refuzo</Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Aktiviteti ───────────────────────────────────────────────────── */}
      {d.history.length > 0 && (
        <Card>
          <CardContent>
            <Typography sx={{ fontWeight: 800, fontSize: '.92rem', mb: 1 }}>Aktiviteti</Typography>
            <Stack spacing={0.5}>
              {[...d.history].reverse().map((e, i) => (
                <Stack key={i} direction="row" spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
                    {fmtDate(e.at)}
                  </Typography>
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {(ACTIVITY_TEXT[e.type] || (() => e.type))(e)}
                    {e.actorName ? ` — ${e.actorName}` : ''}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog open={Boolean(reviewFor)} onClose={() => setReviewFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Refuzo dokumentin</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{reviewFor?.originalName}</Typography>
            {/* Arsyeja i shkon punonjësit: pa të, ai do ta ringarkonte të njëjtin
                skedar pa e ditur çfarë ishte gabim. */}
            <TextField autoFocus multiline minRows={2} fullWidth required label="Arsyeja"
              value={reason} onChange={(e) => setReason(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewFor(null)}>Anulo</Button>
          <Button variant="contained" color="error" disabled={!reason.trim() || busy}
            onClick={async () => {
              const r = await run(async () => {
                const x = await myWorkService().reviewDocument(reviewFor.id, 'rejected', reason);
                if (!x?.error) setDocs(await myWorkService().documentsOf(d.id));
                return x;
              });
              if (r) { setReviewFor(null); setReason(''); }
            }}>Refuzo</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog?.kind === 'cancel' ? 'Anulo procesin'
            : dialog?.kind === 'reopen' ? 'Rihap procesin'
            : dialog?.kind === 'block' ? 'Blloko detyrën'
            : 'Kapërce detyrën'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {dialog?.title && <Typography variant="body2" color="text.secondary">{dialog.title}</Typography>}
            {dialog?.kind === 'cancel' && (
              <Alert severity="warning">Detyrat nuk fshihen — ato tregojnë sa punë ishte bërë kur procesi u ndal.</Alert>
            )}
            <TextField autoFocus multiline minRows={2} fullWidth required label="Arsyeja"
              value={reason} onChange={(e) => setReason(e.target.value)} />
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Arsyeja ruhet te aktiviteti i procesit dhe te gjurma e auditimit.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Anulo</Button>
          <Button variant="contained" disabled={!reason.trim() || busy} onClick={submitDialog}>Konfirmo</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
